const toSeconds = (time: string): number => {
    const [h, m, s] = time.split(":").map(Number);
    return h * 3600 + m * 60 + s;
};

const hhmm = (time: string): string => {
    return time.slice(0, 5);
};

const SECONDS_PER_DAY = 24 * 3600;

const isOvernightRange = (
    opensInSeconds: number,
    closesInSeconds: number,
): boolean => {
    return closesInSeconds < opensInSeconds;
};

const isWithinOpeningRange = (
    nowInSeconds: number,
    opensInSeconds: number,
    closesInSeconds: number,
): boolean => {
    if (opensInSeconds === closesInSeconds) {
        return false;
    }

    if (!isOvernightRange(opensInSeconds, closesInSeconds)) {
        return nowInSeconds >= opensInSeconds && nowInSeconds < closesInSeconds;
    }

    return nowInSeconds >= opensInSeconds || nowInSeconds < closesInSeconds;
};

const millisecondsUntilNextOccurrence = (
    nowInSeconds: number,
    targetInSeconds: number,
): number => {
    const secondsUntilTarget =
        targetInSeconds > nowInSeconds
            ? targetInSeconds - nowInSeconds
            : SECONDS_PER_DAY - nowInSeconds + targetInSeconds;

    return secondsUntilTarget * 1000;
};

const describeOpeningHours = (
    opensInSeconds: number,
    closesInSeconds: number,
    unmannedOpensInSeconds: number,
    unmannedClosesInSeconds: number,
) => {
    const hasMannedHours = opensInSeconds !== closesInSeconds;
    const hasUnmannedHours = unmannedOpensInSeconds !== unmannedClosesInSeconds;
    const sharesOpeningTime =
        hasMannedHours &&
        hasUnmannedHours &&
        opensInSeconds === unmannedOpensInSeconds;
    const sharesClosingTime =
        hasMannedHours &&
        hasUnmannedHours &&
        closesInSeconds === unmannedClosesInSeconds;

    return {
        hasDistinctUnmannedExtension:
            sharesOpeningTime && !sharesClosingTime,
        hasDifferentClosingTimes:
            hasMannedHours &&
            hasUnmannedHours &&
            closesInSeconds !== unmannedClosesInSeconds,
    };
};

class OpenStatus {
    constructor() {
        this.initRenderOpenStatus();
    }
    initRenderOpenStatus() {
        const openHoursText = document.querySelector(
            ".js-open-hours",
        ) as HTMLElement;
        if (!openHoursText) return;
        const opens = openHoursText.dataset.opensToday;
        const closes = openHoursText.dataset.closesToday;
        const renderedDayIndex = openHoursText.dataset.renderedDay;
        const opensNext = openHoursText.dataset.opensNext;
        const opensNextDay = openHoursText.dataset.opensNextDay;
        const unmannedOpens = openHoursText.dataset.unmannedOpens;
        const unmannedCloses = openHoursText.dataset.unmannedCloses;
        const i18nOpenNow = openHoursText.dataset.i18nOpenNow;
        const i18nOpenUnmanned = openHoursText.dataset.i18nOpenUnmanned;
        const i18nOpensNext = openHoursText.dataset.i18nOpensNext;
        const i18nOpensTommorrow = openHoursText.dataset.i18nOpensTommorrow;
        const i18nOpensUnmanned = openHoursText.dataset.i18nOpensUnmanned;
        const i18nClosed = openHoursText.dataset.i18nClosed;
        const i18nCloses = openHoursText.dataset.i18nCloses;
        const i18nUnmanned = openHoursText.dataset.i18nUnmanned;
        const unmannedLabelPrefix = `${i18nUnmanned}\u00A0`;

        const updateOpenHours = () => {
            if (
                !opens ||
                !closes ||
                !renderedDayIndex ||
                !opensNext ||
                !unmannedOpens ||
                !unmannedCloses ||
                !i18nOpenNow ||
                !i18nOpenUnmanned ||
                !i18nOpensNext ||
                !i18nOpensTommorrow ||
                !i18nOpensUnmanned ||
                !i18nClosed ||
                !i18nCloses ||
                !i18nUnmanned
            ) {
                return;
            }

            const today: Date = new Date();
            const currentDayIndex: string = (
                (today.getDay() + 6) %
                7
            ).toString();
            const now: string = today.toLocaleTimeString("sv-SE");
            const nowInSeconds: number = toSeconds(now);
            const opensInSeconds: number = toSeconds(opens);
            const closesInSeconds: number = toSeconds(closes);
            const opensNextInSeconds: number = toSeconds(opensNext);
            const unmannedOpensInSeconds: number = toSeconds(unmannedOpens);
            const unmannedClosesInSeconds: number = toSeconds(unmannedCloses);
            const openingHoursRelationship = describeOpeningHours(
                opensInSeconds,
                closesInSeconds,
                unmannedOpensInSeconds,
                unmannedClosesInSeconds,
            );

            let nextChange = 60 * 1000;

            if (currentDayIndex !== renderedDayIndex) {
                location.reload();
                return;
            }
            const isOpen = isWithinOpeningRange(
                nowInSeconds,
                opensInSeconds,
                closesInSeconds,
            );
            const isUnmannedOpen = isWithinOpeningRange(
                nowInSeconds,
                unmannedOpensInSeconds,
                unmannedClosesInSeconds,
            );
            if (isOpen) {
                let closingStatus = `${i18nCloses} ${hhmm(closes)}`;
                if (
                    openingHoursRelationship.hasDifferentClosingTimes &&
                    isWithinOpeningRange(
                        nowInSeconds,
                        unmannedOpensInSeconds,
                        unmannedClosesInSeconds,
                    )
                ) {
                    closingStatus += ` (${unmannedLabelPrefix}${hhmm(unmannedCloses)})`;
                }

                openHoursText.textContent = `${i18nOpenNow}, ${closingStatus}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    closesInSeconds,
                );
            } else if (isUnmannedOpen) {
                // displays "open (unmanned) now, closes *time*"
                openHoursText.textContent = `${i18nOpenUnmanned}, ${i18nCloses} ${hhmm(unmannedCloses)}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    unmannedClosesInSeconds,
                );
            } else if (
                unmannedOpensInSeconds !== 0 &&
                nowInSeconds < unmannedOpensInSeconds
            ) {
                // displays "closed, opens (unmanned) *time*
                openHoursText.textContent = `${i18nClosed}, ${i18nOpensUnmanned} ${hhmm(unmannedOpens)}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    unmannedOpensInSeconds,
                );
            } else if (nowInSeconds <= opensInSeconds) {
                // displays "closed, opens *time*"
                openHoursText.textContent = `${i18nClosed}, ${i18nOpensNext} ${hhmm(opens)}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    opensInSeconds,
                );
            } else if (opensNextDay) {
                // displays "closed, opens *day* *time*"
                openHoursText.textContent = `${i18nClosed}, ${i18nOpensNext} ${opensNextDay} ${hhmm(opensNext)}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    opensNextInSeconds,
                );
            } else {
                // displays "closes, opens tommorrow *time*"
                openHoursText.textContent = `${i18nClosed}, ${i18nOpensTommorrow} ${hhmm(opensNext)}`;
                nextChange = millisecondsUntilNextOccurrence(
                    nowInSeconds,
                    opensNextInSeconds,
                );
            }
            setTimeout(updateOpenHours, nextChange);
        };

        updateOpenHours();

        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) {
                updateOpenHours();
            }
        });
    }
}

export default OpenStatus;
